/**
 * AttendanceLiveMonitor widget — the live punch feed.
 *
 * 2026-06-16 redesign (owner: "monitor 的精髓"): shows EVERY punch event,
 * newest first — a worker who punches 5 times today shows 5 rows, not one
 * derived per-day summary. Backed by GET /v1/attendance/punches (org-wide
 * punch stream). Auto-refreshes every 20s so the operator sees arrivals in
 * near-real-time without reloading.
 *
 * Each row: worker avatar + name, punch time, location + in/out-of-zone,
 * selfie thumbnail (if captured), and an "invalid" flag for punches the
 * calc rejected (mock GPS / outside fence / face mismatch). Newest 20.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Avatar,
  Box,
  Chip,
  CircularProgress,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Paper,
  Typography,
} from '@mui/material';
import { AccessTime } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { ApiError, attendanceApi } from '../../../../api';
import PhotoViewerDialog from '../../../../components/PhotoViewerDialog';
import { WidgetErrorState } from '../../../../components/primitives';
import { useDashboardWidgetVisible } from '../DashboardWidgetViewport';
import type { OrgPunch } from '../../../../api/attendance';
import { useTranslation } from 'react-i18next';
import { formatDashboardTime } from './widgetDisplay';
import { translateApiError } from '../../../../i18n/translateApiError';
import WidgetEmptyState from '../../../../components/primitives/WidgetEmptyState';
import { HistoryOutlined } from '@mui/icons-material';

const POLL_MS = 20000;
const LIMIT = 20;

function avatarColor(workerId: string): string {
  let hash = 0;
  for (let i = 0; i < workerId.length; i += 1) {
    hash = (hash * 31 + workerId.charCodeAt(i)) | 0;
  }
  const palette = ['#3067D1', '#2E7D32', '#6A1B9A', '#00838F', '#C62828', '#E65100', '#4527A0', '#00695C'];
  return palette[Math.abs(hash) % palette.length];
}

const AttendanceLiveMonitor: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [punches, setPunches] = useState<OrgPunch[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedPunch, setSelectedPunch] = useState<OrgPunch | null>(null);
  const widgetVisible = useDashboardWidgetVisible();
  const [pageVisible, setPageVisible] = useState(
    () => typeof document === 'undefined' || document.visibilityState !== 'hidden',
  );
  const generationRef = useRef(0);
  const initialLoadRef = useRef(true);

  const load = useCallback(async (initial: boolean, generation: number) => {
    try {
      const today = new Date();
      const iso = today.toISOString().slice(0, 10);
      const rows = await attendanceApi.timeEntriesApi.orgPunches({
        start_date: iso,
        end_date: iso,
        limit: LIMIT,
      });
      if (generationRef.current !== generation) return;
      setPunches(rows);
      setError(null);
    } catch (e) {
      if (generationRef.current !== generation) return;
      // On a background refresh failure, keep the last good data; only
      // surface the error on the initial load.
      if (initial) {
        setError(
          e instanceof ApiError ? translateApiError(e, t) : t('dashboard:widgets.attendanceLiveMonitor.loadError'),
        );
      }
    } finally {
      if (generationRef.current === generation && initial) {
        initialLoadRef.current = false;
        setLoading(false);
      }
    }
  }, [t]);

  useEffect(() => {
    const onVisibilityChange = () => setPageVisible(document.visibilityState !== 'hidden');
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, []);

  useEffect(() => {
    if (!widgetVisible || !pageVisible) {
      generationRef.current += 1;
      return undefined;
    }

    const generation = generationRef.current + 1;
    generationRef.current = generation;
    load(initialLoadRef.current, generation);
    const id = window.setInterval(() => load(false, generation), POLL_MS);
    return () => {
      if (generationRef.current === generation) generationRef.current += 1;
      window.clearInterval(id);
    };
  }, [load, pageVisible, widgetVisible]);

  if (loading) {
    return (
      <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (error) {
    return <WidgetErrorState error={error} />;
  }

  if (punches.length === 0) {
    return (
      <WidgetEmptyState icon={HistoryOutlined} title={t('dashboard:widgets.attendanceLiveMonitor.empty')} />
    );
  }

  const selectedWorkerName = selectedPunch?.worker_name?.trim()
    || selectedPunch?.worker_code
    || t('dashboard:widgets.attendanceLiveMonitor.unknownWorker');

  return (
    <>
      <List sx={{ height: '100%', overflow: 'auto', py: 0, mt: -2 }}>
      {punches.map((p, index) => {
        const name = p.worker_name?.trim() || p.worker_code || t('dashboard:widgets.attendanceLiveMonitor.unknownWorker');
        /**
         * The drill-through target.
         *
         * It sent `worker_id=<id>` and Time entries reads `worker` — so the parameter
         * was silently ignored and the click landed on an UNFILTERED month of every
         * worker's punches. Nothing failed: the route exists, the page renders, and the
         * only symptom is a list that is not about the person you clicked (svdy-ui
         * §12.10's family).
         *
         * Two further reasons the id could never have worked: the filter it feeds is a
         * free-text search over names and codes, and it is applied client-side — an
         * opaque id matches nothing. `TodayExceptionsPanel` already sent the NAME, which
         * is the shape this now matches.
         *
         * Encoded, because a name contains a space.
         *
         * One variable rather than the same string twice: the click and the keyboard
         * handler had two copies, which is how one of them would eventually drift from
         * the other.
         */
        const target =
          `/attendance/time-entries?view=events&worker=${encodeURIComponent(name)}`;
        return (
          <ListItem
            key={p.id}
            component={Paper}
            elevation={0}
            role="link"
            tabIndex={0}
            onClick={() => navigate(target)}
            onKeyDown={(event) => {
              if (event.target !== event.currentTarget) return;
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                navigate(target);
              }
            }}
            sx={{
              py: 1,
              mb: index === punches.length - 1 ? 0 : 0.5,
              mt: index === 0 ? 2 : 0,
              borderRadius: 1,
              bgcolor: 'background.paper',
              cursor: 'pointer',
              border: '1px solid transparent',
              transition: 'border-color 120ms',
              '&:hover': { borderColor: 'divider' },
              '&:focus-visible': {
                outline: '2px solid',
                outlineColor: 'primary.main',
                outlineOffset: 2,
              },
              opacity: p.is_valid ? 1 : 0.7,
            }}
          >
            <ListItemAvatar>
              <Avatar
                src={p.worker_avatar_url ?? undefined}
                sx={{ bgcolor: avatarColor(p.worker_id), color: '#fff', fontWeight: 600 }}
              >
                {name.charAt(0).toUpperCase()}
              </Avatar>
            </ListItemAvatar>
            <ListItemText
              primary={
                <Typography variant="body2" sx={{ fontWeight: 600, color: 'primary.main', '&:hover': { textDecoration: 'underline' } }}>
                  {name}
                </Typography>
              }
              secondary={
                <Typography component="div" variant="body2">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <AccessTime fontSize="small" />
                    <Typography variant="body2" component="span">{formatDashboardTime(p.punched_at, i18n.language)}</Typography>
                    {p.address && (
                      <>
                        <Typography variant="body2" component="span" sx={{ mx: 0.5, color: 'text.disabled' }}>·</Typography>
                        <Typography
                          variant="caption"
                          component="span"
                          sx={{ color: 'text.secondary', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 180 }}
                        >
                          {p.address}
                        </Typography>
                      </>
                    )}
                  </Box>
                  {p.within_geofence != null && (
                    <Chip
                      label={
                        p.within_geofence
                          ? t('dashboard:widgets.attendanceLiveMonitor.inZone')
                          : t('dashboard:widgets.attendanceLiveMonitor.outZone')
                      }
                      size="small"
                      color={p.within_geofence ? 'success' : 'warning'}
                      variant="outlined"
                      sx={{ mt: 0.5, height: 18, fontSize: '0.62rem', borderRadius: 1 }}
                    />
                  )}
                </Typography>
              }
            />
            {p.photo_url && (
              <IconButton
                size="small"
                aria-label={t('dashboard:widgets.attendanceLiveMonitor.viewPhoto', { name })}
                onClick={(event) => {
                  event.stopPropagation();
                  setSelectedPunch(p);
                }}
                sx={{ p: 0, mr: 1, borderRadius: 1 }}
              >
                <Avatar
                  variant="rounded"
                  src={p.photo_url}
                  alt=""
                  sx={{ width: 34, height: 34, cursor: 'zoom-in', border: '1px solid', borderColor: 'divider' }}
                />
              </IconButton>
            )}
            <Chip
              label={
                p.is_valid
                  ? t('dashboard:widgets.attendanceLiveMonitor.punch')
                  : t('dashboard:widgets.attendanceLiveMonitor.invalid')
              }
              size="small"
              color={p.is_valid ? 'success' : 'error'}
              variant={p.is_valid ? 'filled' : 'outlined'}
              sx={{ minWidth: 60 }}
            />
          </ListItem>
        );
      })}
      </List>
      <PhotoViewerDialog
        open={selectedPunch != null}
        url={selectedPunch?.photo_url}
        title={selectedPunch
          ? t('dashboard:widgets.attendanceLiveMonitor.photoTitle', {
            name: selectedWorkerName,
            time: formatDashboardTime(selectedPunch.punched_at, i18n.language),
          })
          : undefined}
        onClose={() => setSelectedPunch(null)}
      />
    </>
  );
};

export default AttendanceLiveMonitor;
